const DEBUG_LOG_STORAGE_KEY = "aipriDebugLog";
const DEBUG_LOG_EVENT_NAME = "aipriDebugLogUpdated";
const MAX_DEBUG_LOG_ENTRIES = 300;

export interface DebugLogEntry {
  timestamp: string;
  scope: string;
  message: string;
  details?: string;
}

export const appendDebugLog = (
  scope: string,
  message: string,
  details?: unknown
) => {
  const entry: DebugLogEntry = {
    timestamp: new Date().toISOString(),
    scope,
    message,
    details: formatDetails(details),
  };

  try {
    const logs = [...getDebugLogs(), entry].slice(-MAX_DEBUG_LOG_ENTRIES);
    localStorage.setItem(DEBUG_LOG_STORAGE_KEY, JSON.stringify(logs));
    window.dispatchEvent(new CustomEvent(DEBUG_LOG_EVENT_NAME));
  } catch (error) {
    console.warn("Failed to write debug log", error);
  }
};

export const getDebugLogs = (): DebugLogEntry[] => {
  try {
    const raw = localStorage.getItem(DEBUG_LOG_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(isDebugLogEntry);
  } catch {
    return [];
  }
};

export const getDebugLogText = () => {
  const logs = getDebugLogs();
  if (logs.length === 0) return "ログはまだありません。";

  return logs
    .map((log) => {
      const detailText = log.details ? ` ${log.details}` : "";
      return `[${log.timestamp}] [${log.scope}] ${log.message}${detailText}`;
    })
    .join("\n");
};

export const clearDebugLogs = () => {
  localStorage.removeItem(DEBUG_LOG_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(DEBUG_LOG_EVENT_NAME));
};

export const addDebugLogListener = (listener: () => void) => {
  window.addEventListener(DEBUG_LOG_EVENT_NAME, listener);
  return () => window.removeEventListener(DEBUG_LOG_EVENT_NAME, listener);
};

const formatDetails = (details: unknown) => {
  if (typeof details === "undefined") return undefined;
  if (details instanceof Error) return `${details.name}: ${details.message}`;
  if (typeof details === "string") return details;

  try {
    return JSON.stringify(details);
  } catch {
    return String(details);
  }
};

const isDebugLogEntry = (value: unknown): value is DebugLogEntry => {
  if (typeof value !== "object" || value === null) return false;

  const entry = value as Partial<DebugLogEntry>;
  return (
    typeof entry.timestamp === "string" &&
    typeof entry.scope === "string" &&
    typeof entry.message === "string" &&
    (typeof entry.details === "undefined" || typeof entry.details === "string")
  );
};
